import React, { createContext, useState, useCallback, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

export const OptimisticMessagesContext = createContext();

const OPTIMISTIC_STORAGE_KEY = "crmOptimisticMessages";
const OPTIMISTIC_TTL_MS = 60 * 60 * 1000; // 1 hora

const loadPersistedOptimisticMessages = () => {
  try {
    const raw = localStorage.getItem(OPTIMISTIC_STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};

    const now = Date.now();
    const result = {};

    Object.keys(parsed).forEach((ticketId) => {
      const list = Array.isArray(parsed[ticketId]) ? parsed[ticketId] : [];
      result[ticketId] = list.map((msg) => {
        const createdAtMs = Date.parse(msg?.createdAt);
        const isStale = Number.isFinite(createdAtMs) && now - createdAtMs >= OPTIMISTIC_TTL_MS;
        if (isStale && msg.ack !== -1) {
          return { ...msg, ack: -1 };
        }
        return msg;
      });
    });

    return result;
  } catch (error) {
    return {};
  }
};

export const OptimisticMessagesProvider = ({ children }) => {
  // Estado: { ticketId: [{ optimisticId, body, quotedMsg, createdAt, userName, ... }] }
  const [optimisticMessages, setOptimisticMessages] = useState(loadPersistedOptimisticMessages);
  const [scrollTrigger, setScrollTrigger] = useState(0);

  // Persiste as mensagens otimistas para sobreviver a um reload da página
  useEffect(() => {
    try {
      localStorage.setItem(OPTIMISTIC_STORAGE_KEY, JSON.stringify(optimisticMessages));
    } catch (error) {
      // Quota excedida ou erro de serialização: segue funcionando só em memória
    }
  }, [optimisticMessages]);

  /**
   * Adiciona uma mensagem otimista para um ticket
   * @param {number} ticketId - ID do ticket
   * @param {object} messageData - Dados da mensagem { body, quotedMsg, userName, signMessage }
   * @returns {string} optimisticId - ID único da mensagem otimista
   */
  const addOptimisticMessage = useCallback((ticketId, messageData) => {
    const optimisticId = uuidv4();
    
    const optimisticMessage = {
      optimisticId,
      id: optimisticId, // Usar o mesmo ID como ID temporário
      body: messageData.body,
      quotedMsg: messageData.quotedMsg,
      userName: messageData.userName,
      signMessage: messageData.signMessage,
      createdAt: new Date().toISOString(),
      fromMe: true,
      ack: 0, // ACK 0 = relógio (pendente)
      read: true,
      mediaUrl: null,
      mediaType: null,
      isDeleted: false,
      isOptimistic: true, // Flag para identificar mensagem otimista
      ticketId: ticketId
    };

    setOptimisticMessages(prev => ({
      ...prev,
      [ticketId]: [...(prev[ticketId] || []), optimisticMessage]
    }));

    // Disparar scroll trigger
    setScrollTrigger(prev => prev + 1);

    return optimisticId;
  }, []);

  /**
   * Remove uma mensagem otimista quando a mensagem real chega
   * @param {number} ticketId - ID do ticket
   * @param {string} optimisticId - ID da mensagem otimista a ser removida
   */
  const removeOptimisticMessage = useCallback((ticketId, optimisticId) => {
    setOptimisticMessages(prev => {
      const ticketMessages = prev[ticketId] || [];
      const filtered = ticketMessages.filter(msg => msg.optimisticId !== optimisticId);
      
      if (filtered.length === 0) {
        const { [ticketId]: removed, ...rest } = prev;
        return rest;
      }
      
      return {
        ...prev,
        [ticketId]: filtered
      };
    });
  }, []);

  /**
   * Move uma mensagem otimista de um ticket para outro.
   *
   * No chat de contato a primeira mensagem é criada antes do chamado existir, sob
   * a chave `contact-<id>`. Quando o backend devolve o ticket real, a bolha precisa
   * migrar para essa chave — a reconciliação do MessagesList remove pelo ticketId
   * que vem no socket, então sem isso a bolha pendente ficaria órfã.
   *
   * @param {number|string} fromTicketId - chave atual
   * @param {number|string} toTicketId - chave de destino
   * @param {string} optimisticId - ID da mensagem otimista
   */
  const reassignOptimisticMessage = useCallback((fromTicketId, toTicketId, optimisticId) => {
    if (!fromTicketId || !toTicketId || String(fromTicketId) === String(toTicketId)) {
      return;
    }

    setOptimisticMessages(prev => {
      const source = prev[fromTicketId] || [];
      const moving = source.find(msg => msg.optimisticId === optimisticId);

      if (!moving) {
        return prev;
      }

      const remaining = source.filter(msg => msg.optimisticId !== optimisticId);
      const next = { ...prev };

      if (remaining.length === 0) {
        delete next[fromTicketId];
      } else {
        next[fromTicketId] = remaining;
      }

      next[toTicketId] = [
        ...(next[toTicketId] || []),
        { ...moving, ticketId: toTicketId }
      ];

      return next;
    });
  }, []);

  /**
   * Obtém todas as mensagens otimistas de um ticket
   * @param {number} ticketId - ID do ticket
   * @returns {array} Lista de mensagens otimistas
   */
  const getOptimisticMessages = useCallback((ticketId) => {
    return optimisticMessages[ticketId] || [];
  }, [optimisticMessages]);

  /**
   * Limpa todas as mensagens otimistas de um ticket
   * @param {number} ticketId - ID do ticket
   */
  const clearOptimisticMessages = useCallback((ticketId) => {
    setOptimisticMessages(prev => {
      const { [ticketId]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  return (
    <OptimisticMessagesContext.Provider
      value={{
        optimisticMessages,
        addOptimisticMessage,
        removeOptimisticMessage,
        reassignOptimisticMessage,
        getOptimisticMessages,
        clearOptimisticMessages,
        scrollTrigger
      }}
    >
      {children}
    </OptimisticMessagesContext.Provider>
  );
};

export default OptimisticMessagesContext;
